// src/utils/healthScoreChart.js
//
// The Health Score graph's pure logic, shared by the on-screen chart (pages/PatientTodayView.jsx, the Overview tab and the Today
// screen) and the static SVG drawn into the patient PDF (buildHealthScoreSvg below), so the two cannot drift.
import { formatMarkerText } from './markerNames';

const formatShortDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

export function toHealthScore(riskScore) {
  return riskScore == null ? null : Math.max(0, Math.min(100, 100 - riskScore));
}

// Interpolates a Y value (height on the score line) for a date that isn't
// one of the scored points — used for "Started care" (patient
// registration), which carries no health score of its own but should
// still sit ON the existing baseline→latest curve at its own
// chronological position, the same way a real reading would. X is NOT
// interpolated any more — see insertStartedCare/buildTimeline below: every
// node, scored or not, now gets an equally spaced slot by its position in
// the sequence, not by date, so only the vertical position needs solving
// for here. Clamps to the first/last scored point when the date is
// outside the scored range entirely.
export function interpolateYForDate(scoredPoints, dateStr) {
  if (!scoredPoints.length) return null;
  const t = new Date(dateStr).getTime();
  if (Number.isNaN(t)) return null;
  const lastIdx = scoredPoints.length - 1;
  if (t <= new Date(scoredPoints[0].date).getTime()) return scoredPoints[0].y;
  if (t >= new Date(scoredPoints[lastIdx].date).getTime()) return scoredPoints[lastIdx].y;
  for (let i = 0; i < lastIdx; i++) {
    const t0 = new Date(scoredPoints[i].date).getTime();
    const t1 = new Date(scoredPoints[i + 1].date).getTime();
    if (t >= t0 && t <= t1) {
      const frac = t1 === t0 ? 0 : (t - t0) / (t1 - t0);
      return scoredPoints[i].y + frac * (scoredPoints[i + 1].y - scoredPoints[i].y);
    }
  }
  return scoredPoints[lastIdx].y;
}

// Inserts "Started care" (the patient's registration date) into the
// scored baseline/latest sequence as a real, dated node, at its actual
// chronological position — registration is not guaranteed to be
// chronologically first: a report's draw date can predate registration
// (history the patient brought with them — see V90's migration comment on
// registeredAt), so it can land before baseline, between baseline and
// latest, or (rarer) after latest.
//
// This used to be a separate floating overlay, positioned by
// interpolating BOTH x and y onto the baseline→latest line. That gave it
// no date of its own to show — nothing else ever read startedAt's actual
// value — and whenever it interpolated on or past the latest point's
// date (i.e. registration landed at or after the most recent report), it
// collapsed onto that point's exact (x,y) with nothing to tell the two
// apart. Folding it into the same sequence every other point renders
// through fixes both: it gets the standard date/label treatment in
// HealthScoreChart's shared per-point rendering, and its own equally
// spaced slot instead of a shared one.
export function insertStartedCare(scoredPast, startedAt) {
  if (!startedAt) return scoredPast;
  const entry = { date: startedAt, label: 'Started care', healthScore: null, kind: 'started' };
  const t = new Date(startedAt).getTime();
  const insertAt = scoredPast.findIndex(n => t < new Date(n.date).getTime());
  const result = [...scoredPast];
  result.splice(insertAt === -1 ? result.length : insertAt, 0, entry);
  return result;
}

// Catmull-Rom-to-Bezier smoothing (tension 1/6, the standard value) — turns
// the straight point-to-point polyline into the soft S-curve the mockup
// draws. Each segment's control points lean on the point before and after
// it; at the ends, where there's no such neighbor, the segment's own
// endpoint is reused instead, which is the usual clamp for this formula.
export function smoothPathCommands(pts) {
  let d = '';
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }
  return d;
}

// Baseline and latest scored milestone (health score, ascending is
// healthier), plus one optional future/tentative point — at most 3 labeled
// points on this compact hero chart (see the in-between-milestones note
// below). Both this chart and the parent's trend/"Since" text call this
// same function, so "baseline" always means the same report in both places
// (the patient's true first scored report, not a capped window), and
// "latest" always means the same report the big score number up top does.
export function buildScoreNodes(milestones, nextMilestone) {
  const allPast = (milestones || [])
    .filter(m => m.score != null)
    .map(m => ({ date: m.date, label: formatMarkerText(m.label), healthScore: toHealthScore(m.score), kind: 'past' }));
  // Baseline + latest only — any milestones in between (a clinical finding
  // like "TG/HDL Ratio flagged for review") pushed this compact hero chart
  // to 3-4 labeled points sharing LABEL_W's fixed 80px, truncating every
  // label past "Baseline" into an unreadable fragment. The full milestone
  // history still has a home: the Health Story page's own timeline.
  const past = allPast.length > 2 ? [allPast[0], allPast[allPast.length - 1]] : allPast;
  if (!nextMilestone) return past;
  return [...past, { date: nextMilestone.date, label: formatMarkerText(nextMilestone.label), healthScore: null, kind: 'future', tentative: nextMilestone.tentative }];
}

export function computeTrendPct(past) {
  if (past.length < 2) return null;
  const baseline = past[0].healthScore;
  const latest = past[past.length - 1].healthScore;
  if (!baseline) return null;
  return Math.round(((latest - baseline) / baseline) * 100);
}

// ── static SVG for the PDF ──────────────────────────────────────────────────
// Same layout rules as HealthScoreChart (equal spacing, labels alternating below/above, solid line, dashed amber run to the planned
// follow-up) at a fixed width, with plain <text> labels instead of the screen's foreignObject. pal: { teal, amber, muted, border, surface, font, intervention }.
const W = 600, H = 206, PAD_X = 12, LABEL_W = 130, LABEL_H = 27, LABEL_GAP = 14, PAD_TOP = 48, PAD_BOTTOM = LABEL_GAP + LABEL_H + 9;
const xmlEsc = (v) => String(v ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const clip = (t, n) => (t.length > n ? `${t.slice(0, n - 1)}…` : t);

/** null when the journey has no scored report. */
export function buildHealthScoreSvg(journey, pal) {
  const past = buildScoreNodes(journey?.milestones, null).map((p, i) => ({ ...p, isBaseline: i === 0 }));
  if (!past.length) return null;
  const withFuture = buildScoreNodes(journey.milestones, journey.nextMilestone);
  const future = withFuture.length > past.length ? withFuture[withFuture.length - 1] : null;
  const timelineBase = insertStartedCare(past, journey.startedAt);
  const timeline = future ? [...timelineBase, future] : timelineBase;
  const n = timeline.length;

  const latestScore = past[past.length - 1].healthScore;
  const scores = past.map(p => p.healthScore);
  const axisMin = Math.max(0, Math.floor((Math.min(...scores) - 8) / 10) * 10);
  const axisMax = Math.min(100, Math.ceil((Math.max(...scores) + 8) / 10) * 10);
  const axisRange = Math.max(10, axisMax - axisMin);
  const plotH = H - PAD_TOP - PAD_BOTTOM;
  const spacing = n > 1 ? (W - PAD_X * 2) / (n - 1) : 0;

  const scored = [];
  const withXY = timeline.map((node, i) => {
    const x = PAD_X + i * spacing;
    let y = null;
    if (node.kind === 'past') { y = PAD_TOP + plotH * (1 - (node.healthScore - axisMin) / axisRange); scored.push({ date: node.date, y }); }
    else if (node.kind === 'future') y = PAD_TOP + plotH * (1 - (latestScore - axisMin) / axisRange);
    return { ...node, x, y };
  });
  const points = withXY.map(p => (p.kind === 'started' ? { ...p, y: interpolateYForDate(scored, p.date) } : p));
  const latestIdx = points.map(p => p.kind).lastIndexOf('past');
  const solid = points.filter(p => p.kind !== 'future');
  const line = solid.length > 1 ? `M ${solid[0].x},${solid[0].y}${smoothPathCommands(solid)}` : null;
  const area = solid.length > 1
    ? `M ${PAD_X},${H - PAD_BOTTOM} L ${solid[0].x},${solid[0].y}${smoothPathCommands(solid)} L ${solid[solid.length - 1].x},${H - PAD_BOTTOM} Z` : null;
  const dashed = future ? `${solid[solid.length - 1].x},${solid[solid.length - 1].y} ${points[n - 1].x},${points[n - 1].y}` : null;

  const marks = points.map((p, i) => {
    const isLatest = i === latestIdx, isFuture = p.kind === 'future', isStarted = p.kind === 'started';
    const solidDot = isLatest;
    const iv = pal.intervention || pal.teal; // "Started care" = Previu intervention: blue dot + ring + halo
    const r = isLatest ? 7 : isFuture ? 6 : isStarted ? 5 : 4.5;
    const above = i % 2 === 1;
    const ly = above ? PAD_TOP - LABEL_GAP - LABEL_H : H - PAD_BOTTOM + LABEL_GAP;
    const natural = p.x - LABEL_W / 2;
    const lx = Math.max(0, Math.min(W - LABEL_W, natural));
    const anchor = natural < lx ? 'start' : natural > lx ? 'end' : 'middle';
    const tx = anchor === 'start' ? lx : anchor === 'end' ? lx + LABEL_W : lx + LABEL_W / 2;
    const text = p.isBaseline ? 'Baseline' : isFuture ? `${p.label} →` : p.label;
    return `<g>
  ${isLatest ? `<circle cx="${p.x}" cy="${p.y}" r="${r * 3.4}" fill="${pal.teal}" fill-opacity=".14"/>` : ''}
  ${above ? `<line x1="${p.x}" y1="${PAD_TOP - 2}" x2="${p.x}" y2="${ly + LABEL_H + 2}" stroke="${pal.border}" stroke-width="1"/>` : ''}
  ${!isLatest && (p.healthScore != null || isStarted) ? `<text x="${p.x}" y="${p.y - r - 4}" text-anchor="middle" font-size="12" font-weight="700" fill="${pal.teal}" font-family="${pal.font}">${xmlEsc(p.healthScore ?? Math.round(axisMin + axisRange * (1 - (p.y - PAD_TOP) / plotH)))}</text>` : ''}
  ${isStarted ? `<circle cx="${p.x}" cy="${p.y}" r="15" fill="${iv}" fill-opacity=".18"/><circle cx="${p.x}" cy="${p.y}" r="11" fill="none" stroke="${iv}" stroke-opacity=".45" stroke-width="1.25"/>` : ''}
  <circle cx="${p.x}" cy="${p.y}" r="${r}" fill="${isStarted ? iv : solidDot ? pal.teal : pal.surface}" stroke="${isFuture ? pal.amber : solidDot ? 'none' : pal.teal}" stroke-width="${isFuture ? 1.5 : solidDot ? 0 : 2}"${isFuture ? ' stroke-dasharray="2 2"' : ''}/>
  <text x="${tx}" y="${ly + 10}" text-anchor="${anchor}" font-size="10" fill="${pal.muted}" font-family="${pal.font}">${xmlEsc(formatShortDate(p.date))}</text>
  <text x="${tx}" y="${ly + 23}" text-anchor="${anchor}" font-size="11" font-weight="${isLatest || isFuture ? 700 : 500}" fill="${isFuture ? pal.amber : isStarted ? iv : solidDot ? pal.teal : pal.muted}" font-family="${pal.font}">${xmlEsc(clip(text, 24))}</text>
</g>`;
  }).join('');

  return `<svg viewBox="0 0 ${W} ${H}" width="100%" style="display:block" role="img" aria-label="Health score trend">
  <defs><linearGradient id="hsFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="${pal.teal}" stop-opacity=".5"/><stop offset="45%" stop-color="${pal.teal}" stop-opacity=".18"/><stop offset="100%" stop-color="${pal.teal}" stop-opacity="0"/></linearGradient></defs>
  ${area ? `<path d="${area}" fill="url(#hsFill)"/>` : ''}
  ${line ? `<path d="${line}" fill="none" stroke="${pal.teal}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>` : ''}
  ${dashed ? `<polyline points="${dashed}" fill="none" stroke="${pal.amber}" stroke-width="2" stroke-dasharray="5 5" stroke-linecap="round"/>` : ''}
  ${marks}
</svg>`;
}

/** The card around it: the graph, then "Health score" and the change since the baseline, as on the Overview tab. */
export function healthScoreCardHtml(journey, pal) {
  const svg = buildHealthScoreSvg(journey, pal);
  if (!svg) return '';
  const nodes = buildScoreNodes(journey.milestones, null);
  const score = nodes.length ? nodes[nodes.length - 1].healthScore : null;
  const trend = computeTrendPct(nodes);
  return `<div style="font-family:${pal.font};color:${pal.ink};background:${pal.surface};border:1px solid ${pal.border};border-radius:18px;padding:14px 18px 12px">
  ${svg}
  <div style="border-top:1px solid ${pal.border};margin-top:8px;padding-top:9px;display:flex;justify-content:space-between;align-items:flex-end">
    <div><div style="font-weight:700;font-size:.72em;letter-spacing:.12em;text-transform:uppercase;color:${pal.muted}">Health score</div><div style="font-weight:700;font-size:2.2em;line-height:1.1">${score != null ? Math.round(score) : '—'}</div></div>
    ${trend != null ? `<div style="text-align:right;font-size:.8em;color:${pal.muted}">Since ${xmlEsc(formatShortDate(nodes[0].date))}<div style="font-weight:700;font-size:1.1em;color:${trend >= 0 ? pal.teal : pal.amber}">● ${trend >= 0 ? `+${trend}% improved` : `${trend}% since baseline`}</div></div>` : ''}
  </div>
</div>`;
}
